/* Korean Essence cart drawer: every change goes through the Cart AJAX API and the drawer is re-rendered
   from Liquid with the Section Rendering API, so the markup lives in one place (sections/ke-cart-drawer.liquid). */
(function () {
  if (customElements.get('ke-cart-drawer')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_LINKS = '#cart-icon-bubble, a[href="/cart"], a[href$="/cart"]';

  class KeCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.index = 0;
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('ke:cart:open', (event) => {
        if (event.detail && event.detail.sections) this.render(event.detail.sections);
        this.open();
      });
      if (this.dataset.takeover === 'true') {
        /* UpCart still sees our cart calls and "opens" its hidden drawer, which locks page scrolling */
        const unlock = () => {
          if (document.body.classList.contains('upcartPopupShow')) document.body.classList.remove('upcartPopupShow');
        };
        new MutationObserver(unlock).observe(document.body, { attributes: true, attributeFilter: ['class'] });
        unlock();
        this.watchAdds();
        window.addEventListener('click', (event) => {
          const link = event.target.closest(CART_LINKS);
          if (!link || this.contains(link)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.refresh().then(() => this.open());
        }, true);
      }
      this.afterRender();
    }

    /* Every other add-to-cart on the site (Kaching on other products, collection buttons, sticky bar,
       story slider) posts to /cart/add. When one succeeds, show the cart in this drawer. Our own calls
       carry X-KE-Cart and are skipped. */
    watchAdds() {
      if (window.__keCartWatch) return;
      window.__keCartWatch = true;
      const drawer = this;
      const isAdd = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
      const show = () => drawer.refresh().then(() => drawer.open());
      const originalFetch = window.fetch;
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const headers = (init && init.headers) || (input && input.headers);
        const own = headers && (headers['X-KE-Cart'] || (typeof headers.get === 'function' && headers.get('X-KE-Cart')));
        const result = originalFetch.apply(this, arguments);
        if (!own && isAdd(url)) {
          result.then((response) => { if (response.ok) show(); }).catch(() => {});
        }
        return result;
      };
      const open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        if (isAdd(url)) this.addEventListener('load', () => { if (this.status >= 200 && this.status < 300) show(); });
        return open.apply(this, arguments);
      };
    }

    closeThemeDrawer() {
      const theme = document.querySelector('cart-drawer');
      if (theme && theme.classList.contains('active') && typeof theme.close === 'function') theme.close();
    }

    get inner() {
      return this.querySelector('.ke-cd__inner');
    }

    open() {
      this.closeThemeDrawer();
      setTimeout(() => this.closeThemeDrawer(), 400);
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.documentElement.classList.add('ke-cd-lock');
      this.querySelector('.ke-cd__panel').focus({ preventScroll: true });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('ke-cd-lock');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    render(sections) {
      const html = sections && sections[this.sectionId];
      if (!html) return;
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('.ke-cd__inner');
      if (next) this.inner.replaceWith(next);
      this.afterRender();
    }

    async refresh() {
      /* Ask the cart page: Shopify can serve other pages (collections) from cache with a stale, empty cart */
      const url = root + 'cart?sections=' + encodeURIComponent(this.sectionId);
      try {
        /* No Accept: application/json here: Shopify answers that with cart JSON instead of the section HTML */
        const response = await fetch(url, { headers: { 'X-KE-Cart': '1' }, cache: 'no-store' });
        if (response.ok) this.render(await response.json());
      } catch (error) {
        /* keep the last rendered cart */
      }
    }

    async post(path, body) {
      this.inner.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(root + path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-KE-Cart': '1' },
          body: JSON.stringify(Object.assign({}, body, {
            sections: this.sectionId,
            sections_url: root + 'cart'
          }))
        });
        const json = await response.json();
        if (!response.ok) throw new Error(json.description || json.message || 'cart update failed');
        this.render(json.sections);
      } catch (error) {
        this.inner.removeAttribute('aria-busy');
        const message = this.querySelector('.ke-cd__error');
        if (message) message.hidden = false;
      }
    }

    onClick(event) {
      const target = event.target.closest('[data-ke-close], [data-ke-qty], [data-ke-cd-add], [data-ke-protect], [data-ke-prev], [data-ke-next]');
      if (!target) return;
      if (target.hasAttribute('data-ke-close')) return this.close();
      if (target.hasAttribute('data-ke-qty')) {
        const line = target.closest('[data-key]');
        return this.post('cart/change.js', { id: line.dataset.key, quantity: Number(target.dataset.keQty) });
      }
      if (target.hasAttribute('data-ke-cd-add')) {
        return this.post('cart/add.js', { items: [{ id: Number(target.dataset.keCdAdd), quantity: 1 }] });
      }
      if (target.hasAttribute('data-ke-protect')) {
        const key = target.dataset.lineKey;
        if (key) return this.post('cart/change.js', { id: key, quantity: 0 });
        const id = Number(target.dataset.variantId);
        return this.post('cart/add.js', {
          items: [{ id, quantity: 1, properties: { __upcartAddon: id, __upcartShippingProtection: true } }]
        });
      }
      if (target.hasAttribute('data-ke-prev')) this.index -= 1;
      if (target.hasAttribute('data-ke-next')) this.index += 1;
      this.slide(true);
    }

    onChange(event) {
      const input = event.target.closest('.ke-cd__qty input');
      if (!input) return;
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);
      this.post('cart/change.js', { id: input.closest('[data-key]').dataset.key, quantity });
    }

    /* Arrows scroll the window; a swipe scrolls it natively and the arrows follow */
    slide(smooth) {
      const win = this.querySelector('.ke-cd__window');
      if (!win) return;
      const count = win.querySelectorAll('.ke-cd__slide').length;
      this.index = Math.max(0, Math.min(this.index, count - 1));
      win.scrollTo({ left: this.index * win.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      this.arrows(count);
    }

    arrows(count) {
      const prev = this.querySelector('[data-ke-prev]');
      const next = this.querySelector('[data-ke-next]');
      if (prev) prev.disabled = this.index === 0;
      if (next) next.disabled = this.index >= count - 1;
    }

    watchSwipe() {
      const win = this.querySelector('.ke-cd__window');
      if (!win) return;
      let timer;
      let touching = false;
      /* CSS snapping does the work; this only finishes a swipe that the browser left between two products */
      const settle = () => {
        const width = win.clientWidth;
        if (!width || touching) return;
        this.index = Math.round(win.scrollLeft / width);
        this.arrows(win.querySelectorAll('.ke-cd__slide').length);
        if (Math.abs(win.scrollLeft - this.index * width) > 2) {
          win.scrollTo({ left: this.index * width, behavior: 'smooth' });
        }
      };
      win.addEventListener('touchstart', () => { touching = true; }, { passive: true });
      ['touchend', 'touchcancel'].forEach((type) => {
        win.addEventListener(type, () => {
          touching = false;
          clearTimeout(timer);
          timer = setTimeout(settle, 120);
        }, { passive: true });
      });
      win.addEventListener('scroll', () => {
        clearTimeout(timer);
        timer = setTimeout(settle, 120);
      }, { passive: true });
    }

    afterRender() {
      this.slide(false);
      this.watchSwipe();
      const inner = this.inner;
      if (!inner) return;
      const count = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
      if (count) count.textContent = inner.dataset.count;
      if (inner.dataset.orphan) this.post('cart/change.js', { id: inner.dataset.orphan, quantity: 0 });
    }
  }

  customElements.define('ke-cart-drawer', KeCartDrawer);
})();
